import { useState } from "react"
import { useSnackbar } from "notistack"
import { download, generateCsv, mkConfig } from "export-to-csv"
import { sleep } from "../../../../../lib/lang"
import { Logger } from "../../../../../lib/logging"
import { toArrayAsync } from "../../../../../lib/iterables"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { AccountingDebtor, DebtorWithSalesByAccount } from "../accountingTypes"
import { getCustomerGroupSalesByAccounts, getCustomerSalesByAccounts } from "../accountingApi"

type Props = Readonly<{
    merchantId: number
    startDate: string
    endDate: string
    debtors: AccountingDebtor[] | undefined
}>

export function ExportButtonDebtors({ merchantId, startDate, endDate, debtors }: Props) {
    const [loading, setLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})

    const { mutateAsync: callGetCustomerSales } = useApiMutation(getCustomerSalesByAccounts, {})
    const { mutateAsync: callGetCustomerGroupSales } = useApiMutation(getCustomerGroupSalesByAccounts, {})

    const periodTranslation = l10n.getString("export-period")

    async function* generateReportData() {
        if (!debtors) return

        for (const debtor of debtors) {
            if (debtor.type === "customer") {
                const sales = await callGetCustomerSales({
                    merchantId,
                    customerId: debtor.id,
                    startDate,
                    endDate,
                })

                const result: DebtorWithSalesByAccount = {
                    sales: sales.salesByAccount,
                    ...debtor,
                }

                yield result
            } else {
                const sales = await callGetCustomerGroupSales({
                    merchantId,
                    customerId: debtor.customerId,
                    customerGroupId: debtor.id,
                    startDate,
                    endDate,
                })

                const result: DebtorWithSalesByAccount = {
                    sales: sales.salesByAccount,
                    ...debtor,
                }

                yield result
            }

            // Throttle the requests to avoid API-side throttling
            await sleep(250)
        }
    }

    function handleExportRows(data: DebtorWithSalesByAccount[]) {
        const csvOptions = mkConfig({
            fieldSeparator: ";",
            quoteStrings: true,
            decimalSeparator: ".",
            useBom: true,
            useKeysAsHeaders: true,
            showColumnHeaders: false,
            filename: `Facilitynet-Debtors-${startDate}_${endDate}`,
        })

        const exportRowsForBalance = data.flatMap((debtor) => [
            {
                column1: debtor.name,
                column2: "",
                column3: "",
                column4: "",
                column5: "",
                column6: "",
            },
            ...debtor.sales.categories.flatMap((category) => [
                {
                    column1: category.name,
                    column2: "",
                    column3: l10n.getString("accounting-debtors-export-items-label"),
                    column4: `${l10n.getString("accounting-debtors-export-amount-label")} (${
                        category.amount.currency
                    })`,
                    column5: l10n.getString("accounting-debtors-export-account-total"),
                    column6: l10n.getString("accounting-debtors-export-subtotal"),
                },
                {
                    column1: "",
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: "",
                },
                ...category.accounts.flatMap((account) => [
                    {
                        column1: account.name,
                        column2: "",
                        column3: "",
                        column4: "",
                        column5: moneyFactory.formatAsNumber(toDinero(account.amount)),
                        column6: "",
                    },
                    ...(account.products ?? []).map((product) => ({
                        column1: product.name,
                        column2: product.code,
                        column3: product.numberOfItems,
                        column4: moneyFactory.formatAsNumber(toDinero(product.totalAmount)),
                        column5: "",
                        column6: "",
                    })),
                ]),
                {
                    column1: "",
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: "",
                },
                {
                    column1: `${l10n.getString("accounting-debtors-export-subtotal-label")} (${category.name})`,
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: moneyFactory.formatAsNumber(toDinero(category.amount)),
                },
                {
                    column1: "",
                    column2: "",
                    column3: "",
                    column4: "",
                    column5: "",
                    column6: "",
                },
            ]),
            {
                column1: "",
                column2: "",
                column3: "",
                column4: "",
                column5: "",
                column6: "",
            },
        ])

        exportRowsForBalance.unshift({
            column1: "",
            column2: "",
            column3: "",
            column4: "",
            column5: "",
            column6: "",
        })

        exportRowsForBalance.unshift({
            column1: `${periodTranslation}: ${startDate} - ${endDate}`,
            column2: "",
            column3: "",
            column4: "",
            column5: "",
            column6: "",
        })

        const csv = generateCsv(csvOptions)(exportRowsForBalance)
        download(csvOptions)(csv)
    }

    function handleClick() {
        const logger = new Logger("debitors")
        setLoading(true)

        toArrayAsync(generateReportData())
            .then((data) => handleExportRows(data))
            .catch((error) => {
                logger.error("Failed to export debitors", error)

                enqueueSnackbar({
                    variant: "error",
                    message: l10n.getString("accounting-debtors-export-failed"),
                })
            })
            .finally(() => setLoading(false))
    }

    return <StandardExportButton loading={loading} onClick={handleClick} />
}
